import bridge, { parseURLSearchParamsForGetLaunchParams } from '@vkontakte/vk-bridge';

import type { Platform, PlatformChrome, PlatformTheme, PlatformViewport } from './types';
import { subscribeDocumentVisibility } from './visibility';

/**
 * Обёртка над `@vkontakte/vk-bridge` — официальным мостом VK Mini Apps
 * (github.com/VKCOM/vk-bridge). Launch params (`vk_user_id`, `sign`, …)
 * VK кладёт в query string при открытии приложения.
 */

/**
 * Launch params живут только в query string и пропадают при смене адреса
 * внутри приложения (SPA-навигация через pushState). Поэтому запоминаем их
 * при загрузке модуля и при каждом чтении, пока они в адресе; дальше
 * платформа определяется и идентифицируется по сохранённой строке.
 * Эта память переживает только SPA-навигацию: при полной перезагрузке она
 * сбрасывается, поэтому перезапуск идёт через `restart`, который сам
 * дописывает launch params к адресу.
 */
let rememberedSearch: string | null = null;

function currentLaunchSearch(): string {
  const search = window.location.search;

  if (parseURLSearchParamsForGetLaunchParams(search).vk_user_id) {
    rememberedSearch = search;
    return search;
  }

  return rememberedSearch ?? search;
}

currentLaunchSearch();

/** Только для тестов — забывает сохранённые launch params между кейсами. */
export function __resetVkLaunchParamsForTests(): void {
  rememberedSearch = null;
}

function launchParams(): URLSearchParams {
  return new URLSearchParams(currentLaunchSearch());
}

/** Приложение открыто внутри VK: launch params появляются только там. */
export function isVkMiniApp(): boolean {
  return Boolean(parseURLSearchParamsForGetLaunchParams(currentLaunchSearch()).vk_user_id);
}

function currentViewport(): PlatformViewport {
  return { height: window.innerHeight, isExpanded: true, safeAreaInsetBottom: 0 };
}

export function createVkPlatform(): Platform {
  // appearance приходит только асинхронно (VKWebAppGetConfig после init,
  // затем события VKWebAppUpdateConfig) — до первого ответа берём дефолт.
  // Тема хоста на оформление приложения не влияет (см. CLAUDE.md).
  let theme: PlatformTheme = { scheme: 'dark' };
  const themeListeners = new Set<(theme: PlatformTheme) => void>();

  function setTheme(appearance: PlatformTheme['scheme']): void {
    theme = { scheme: appearance };
    themeListeners.forEach((listener) => listener(theme));
  }

  // До VKWebAppInit клиент запросы не принимает, а ThemeProvider вызывает
  // setChrome раньше ready() — запоминаем последний цвет и отдаём после init.
  let initialized = false;
  let pendingChrome: PlatformChrome | null = null;

  function sendChrome({ background, scheme }: PlatformChrome): void {
    void bridge
      .send('VKWebAppSetViewSettings', {
        // Цвет значков статус-бара: светлые на тёмном фоне и наоборот.
        status_bar_style: scheme === 'dark' ? 'light' : 'dark',
        action_bar_color: background,
      })
      .catch(() => {});
  }

  // VK скрывает приложение событиями моста: документ при этом может оставаться
  // «видимым», поэтому одного visibilitychange недостаточно.
  const visibilityListeners = new Set<(visible: boolean) => void>();

  function notifyVisibility(visible: boolean): void {
    visibilityListeners.forEach((listener) => listener(visible));
  }

  bridge.subscribe((event) => {
    if (event.detail.type === 'VKWebAppUpdateConfig') {
      setTheme(event.detail.data.appearance);
    } else if (event.detail.type === 'VKWebAppViewHide') {
      notifyVisibility(false);
    } else if (event.detail.type === 'VKWebAppViewRestore') {
      notifyVisibility(true);
    }
  });

  return {
    kind: 'vk',
    accountLinkRole: 'redeemer',
    paymentKind: null,

    ready() {
      // Без VKWebAppInit клиент VK не показывает приложение вовсе — это
      // открытие, а не просто уведомление о готовности. Вне VK-клиента
      // запросы предсказуемо не отвечают — деградируем молча.
      void bridge
        .send('VKWebAppInit')
        .then(() => {
          initialized = true;
          if (pendingChrome) {
            sendChrome(pendingChrome);
            pendingChrome = null;
          }
          return bridge.send('VKWebAppGetConfig');
        })
        .then((config) => setTheme(config.appearance))
        .catch(() => {});
    },

    getIdentity() {
      const params = launchParams();
      const sign = params.get('sign');

      if (!sign || !params.get('vk_user_id')) {
        return null;
      }

      return { platform: 'vk', launchParams: currentLaunchSearch().replace(/^\?/, '') };
    },

    getStartParam() {
      // VK не имеет отдельного канала deep-link payload наподобие
      // Telegram start_param — реферальный код приходит обычным `?ref=`.
      return null;
    },

    getTheme() {
      return theme;
    },

    subscribeTheme(listener) {
      themeListeners.add(listener);
      return () => themeListeners.delete(listener);
    },

    setChrome(chrome) {
      if (initialized) {
        sendChrome(chrome);
      } else {
        pendingChrome = chrome;
      }
    },

    getViewport() {
      return currentViewport();
    },

    subscribeViewport(listener) {
      const handler = () => listener(currentViewport());

      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    },

    subscribeVisibility(listener) {
      visibilityListeners.add(listener);
      const unsubscribeDocument = subscribeDocumentVisibility(listener);

      return () => {
        visibilityListeners.delete(listener);
        unsubscribeDocument();
      };
    },

    setBackHandler() {
      // Системную навигацию VK Mini App не отдаёт приложению для MVP —
      // экраны используют собственную навигацию в интерфейсе.
    },

    close() {
      void bridge.send('VKWebAppClose', { status: 'success' }).catch(() => {});
    },

    async share(payload) {
      await bridge.send('VKWebAppShare', { link: payload.url }).catch(() => {
        // Пользователь мог отменить системный диалог — это не ошибка приложения.
      });
    },

    restart(path) {
      // Подпись клиент не трогает: передаём строку как есть, сервер проверит её
      // при следующем запуске. Без launch params (вне VK) — обычный переход.
      const search = currentLaunchSearch();
      const separator = path.includes('?') ? '&' : '?';
      const query = search.replace(/^\?/, '');
      const hash = path.indexOf('#');
      const base = hash === -1 ? path : path.slice(0, hash);
      const fragment = hash === -1 ? '' : path.slice(hash);

      window.location.assign(query ? `${base}${separator}${query}${fragment}` : path);
    },

    openExternalLink(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    getMessageAccess() {
      return 'unsupported';
    },

    requestMessageAccess() {
      return Promise.resolve(false);
    },

    startPayment() {
      return Promise.resolve('unsupported');
    },
  };
}
